import { CSSProperties, useEffect, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { useShowToast } from '@/design_system/Toast';
import { BatchResultResponse, RequestWithRelationsFromRequestsList } from '@/models/request';

import { BulkResultView } from './BulkResultView';

export const BulkConfirmationDialog = ({
  title,
  confirm,
  onConfirm,
  showDialog,
  setShowDialog,
  confirmButtonVariant = 'primary',
  maxWidth = '28rem',
  isPending,
  isConfirmDisabled,
  children,
  requests,
  onCloseAfterResult,
}: {
  title: React.ReactNode;
  confirm: React.ReactNode;
  onConfirm: () => Promise<BatchResultResponse>;
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  confirmButtonVariant?: 'primary' | 'danger';
  maxWidth?: CSSProperties['maxWidth'];
  isPending?: boolean;
  isConfirmDisabled?: boolean;
  children: React.ReactNode;
  requests: RequestWithRelationsFromRequestsList[];
  onCloseAfterResult: () => void;
}) => {
  const { t } = useLingui();
  const showToast = useShowToast();
  const [result, setResult] = useState<BatchResultResponse | null>(null);

  useEffect(() => {
    if (showDialog) {
      setResult(null);
    }
  }, [showDialog]);

  const close = () => {
    if (isPending) {
      return;
    }

    if (result) {
      onCloseAfterResult();
    }

    setShowDialog(false);
    setResult(null);
  };

  if (!showDialog) {
    return null;
  }

  if (result) {
    return (
      <Dialog
        isOpen
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            close();
          }
        }}
        title={
          result.succeeded.length === 0 ? (
            <Trans id="requests.bulk.result.title.all-failed">No request could be updated</Trans>
          ) : (
            <Trans id="requests.bulk.result.title">Some requests could not be updated</Trans>
          )
        }
        style={{ maxWidth }}
      >
        <main>
          <BulkResultView result={result} requests={requests} />
        </main>
        <Footer>
          <FooterButtons>
            <Button variant="primary" size="medium" onPress={close}>
              <Trans id="requests.bulk.result.close">Close</Trans>
            </Button>
          </FooterButtons>
        </Footer>
      </Dialog>
    );
  }

  return (
    <ConfirmationDialog
      title={title}
      confirm={confirm}
      confirmButtonVariant={confirmButtonVariant}
      maxWidth={maxWidth}
      isPending={isPending}
      isConfirmDisabled={isConfirmDisabled}
      showDialog={showDialog}
      setShowDialog={(show) => {
        if (!show) {
          close();
        }
      }}
      onConfirm={async () => {
        const batchResult = await onConfirm();

        if (batchResult.failed.length === 0) {
          showToast({
            type: 'success',
            text: t({
              id: 'requests.bulk.toast.success',
              message: 'Requests updated',
            }),
          });
          onCloseAfterResult();

          return;
        }

        setResult(batchResult);

        return false;
      }}
    >
      {children}
    </ConfirmationDialog>
  );
};
